import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  IUserChannels,
  IUserLangs,
  IReduxRequestNode,
  IStandardGUJRequestParams,
} from "@litres-libs/types";
import { Request } from "../../../request/Request";
import { addHydrateReducer } from "../../../utils/hydrateReducer";

interface IUserSpamState {
  userChannels: IUserChannels[];
  userLangs: IUserLangs[];
  actions: {
    updateChannelsCSRF: TUpdateSpamCSRFNode;
    getUserChannelsGUJ: TGetUserChannelsGUJNode;
    getUserLangsGUJ: TGetUserLangsGUJNode;
  };
}

const initialState: IUserSpamState = {
  userChannels: [],
  userLangs: [],
  actions: {
    updateChannelsCSRF: {},
    getUserChannelsGUJ: {},
    getUserLangsGUJ: {},
  },
};

type TUpdateSpamCSRFNode = IReduxRequestNode<
  IUpdateSpamGUJRequest,
  IUpdateSpamGUJResponse
>;
type TGetUserChannelsGUJNode = IReduxRequestNode<
  IGetUserChannelsGUJRequest,
  IGetUserChannelsGUJResponse
>;

type IGetUserChannelsGUJRequest = IStandardGUJRequestParams;
type TGetUserLangsGUJNode = IReduxRequestNode<
  IGetUserLangsGUJRequest,
  IGetUserLangsGUJResponse
>;

type IGetUserLangsGUJRequest = IStandardGUJRequestParams;
type IUpdateSpamGUJRequest = IStandardGUJRequestParams & IUpdateSpamGUJParams;

interface IUpdateSpamGUJResponse {
  status: string;
  csrf: string;
}

interface IUpdateSpamGUJParams {
  user_channels: string;
  user_langs: string;
  user_uilang: string;
}

interface IGetUserChannelsGUJResponse {
  userChannels: IUserChannels[];
}

interface IGetUserLangsGUJResponse {
  userLangs: IUserLangs[];
}

export const updateSpamCSRF = createAsyncThunk(
  "pages/personalCabinet/updateSpamCSRF",
  async (params: {
    data: IUpdateSpamGUJParams;
    onSuccess?: (success: TUpdateSpamCSRFNode) => void;
    onError?: (error: TUpdateSpamCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IUpdateSpamGUJRequest,
      IUpdateSpamGUJResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "update_my_channels_and_langs",
      },
      request: {
        method: "POST",
        data: {
          action: "update_my_channels_and_langs",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const getUserLangsGUJ = createAsyncThunk(
  "pages/personalCabinet/getUserLangsGUJ",
  async () => {
    const response = new Request.GUJ<
      IGetUserLangsGUJRequest,
      IGetUserLangsGUJResponse
    >({
      url: "/pages/personal_cabinet_subscriptions/?lite=1&gu_ajax=1&user_langs=1",
      provider: {
        id: "GET_USER_LANGS",
      },
      request: {
        method: "GET",
      },
    });

    return await response.fetch();
  }
);

export const getUserChannelsGUJ = createAsyncThunk(
  "pages/personalCabinet/getUserChannelsGUJ",
  async () => {
    const response = new Request.GUJ<
      IGetUserChannelsGUJRequest,
      IGetUserChannelsGUJResponse
    >({
      url: "/pages/personal_cabinet_subscriptions/?lite=1&gu_ajax=1&user_channels=1",
      provider: {
        id: "GET_USER_CHANNELS",
      },
      request: {
        method: "GET",
      },
    });

    return await response.fetch();
  }
);

export const userSpamSlice = createSlice({
  name: "pages/personalCabinset/userSpam",
  initialState,
  reducers: {
    setUserChannels: (
      state: IUserSpamState,
      action: PayloadAction<IUserChannels[]>
    ) => {
      state.userChannels = action.payload;
    },
    setUserLangs: (
      state: IUserSpamState,
      action: PayloadAction<IUserLangs[]>
    ) => {
      state.userLangs = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "pages.personalCabinet.spam");

    builder.addCase(getUserChannelsGUJ.pending, (state: IUserSpamState) => {
      state.actions.getUserChannelsGUJ.isLoading = true;
      state.actions.getUserChannelsGUJ.error = undefined;
    });
    builder.addCase(
      getUserChannelsGUJ.fulfilled,
      (
        state: IUserSpamState,
        action: PayloadAction<TGetUserChannelsGUJNode>
      ) => {
        state.actions.getUserChannelsGUJ = action.payload;
        state.actions.getUserChannelsGUJ.isLoading = false;

        state.userChannels = action.payload.response?.userChannels
          ? action.payload.response.userChannels
          : [];
      }
    );
    builder.addCase(updateSpamCSRF.pending, (state: IUserSpamState) => {
      state.actions.updateChannelsCSRF.isLoading = true;
      state.actions.updateChannelsCSRF.error = undefined;
    });
    builder.addCase(
      updateSpamCSRF.fulfilled,
      (state: IUserSpamState, action: PayloadAction<TUpdateSpamCSRFNode>) => {
        state.actions.updateChannelsCSRF = action.payload;
        state.actions.updateChannelsCSRF.isLoading = false;
      }
    );
    builder.addCase(getUserLangsGUJ.pending, (state: IUserSpamState) => {
      state.actions.getUserLangsGUJ.isLoading = true;
      state.actions.getUserLangsGUJ.error = undefined;
    });
    builder.addCase(
      getUserLangsGUJ.fulfilled,
      (state: IUserSpamState, action: PayloadAction<TGetUserLangsGUJNode>) => {
        state.actions.getUserLangsGUJ = action.payload;
        state.actions.getUserLangsGUJ.isLoading = false;

        state.userLangs = action.payload.response?.userLangs
          ? action.payload.response.userLangs
          : [];
      }
    );
  },
});

export const { setUserLangs, setUserChannels } = userSpamSlice.actions;

export default userSpamSlice.reducer;
